/**
 * 9F Platform Provably Fair (公平性证明) 算法库
 * 采用加密散列（HMAC-SHA256）保证平台每一局游戏的结果都在开局前确定，且玩家可事后验证，无法被篡改。
 */
export class ProvablyFair {
  
  /**
   * 使用 Web Crypto API 进行 SHA-256 哈希计算 (生成公布给玩家的已哈希服务端种子)
   */
  public static async sha256(text: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * 采用 HMAC-SHA256 算法，根据 服务端种子、客户端种子和Nonce 计算出一个 256 位 HASH
   */
  public static async generateHmac(serverSeed: string, clientSeed: string, nonce: number): Promise<string> {
    const message = `${clientSeed}-${nonce}`;
    const encoder = new TextEncoder();
    const keyData = encoder.encode(serverSeed);
    const messageData = encoder.encode(message);

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signatureBuffer = await crypto.subtle.sign(
      'HMAC',
      cryptoKey,
      messageData
    );

    return Array.from(new Uint8Array(signatureBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * 经典 Crash (爆裂飞行) 游戏结果生成算法
   * @returns 最终爆炸倍数 (例如 1.00x - 1000.00x)
   */
  public static async calculateCrashOutcome(serverSeed: string, clientSeed: string, nonce: number): Promise<number> {
    const hash = await this.generateHmac(serverSeed, clientSeed, nonce);
    
    // 1. 每 100 局中强制出现 3 局的庄家抽水 (3% 概率在 1.00x 瞬间爆炸，即 instant crash)
    const intVal = parseInt(hash.substring(0, 8), 16);
    if (intVal % 100 < 3) {
      return 1.00;
    }

    // 2. 将哈希的前 13 位十六进制转换为数字 (约 2^52 最大值)
    const h = parseInt(hash.substring(0, 13), 16);
    const e = Math.pow(2, 52);

    // 3. 计算倍率公式: multiplier = (100 * e - h) / (e - h) / 100
    // 该数学模型广泛用于 Bustabit 等主流公平链游中
    const multiplier = Math.floor((100 * e - h) / (e - h)) / 100;
    
    // 限制最高倍率 50000.00x 以防极端情况穿仓
    return Math.min(50000, Math.max(1.00, multiplier));
  }

  /**
   * 经典 Slots (老虎机) 卷轴旋转结果生成算法
   * @param reelsCount 滚轮个数 (默认 5 轴)
   * @param symbolsPerReel 每个滚轮包含的图案数 (例如 10 种不同图案)
   */
  public static async calculateSlotsOutcome(
    serverSeed: string,
    clientSeed: string,
    nonce: number,
    reelsCount: number = 5,
    symbolsPerReel: number = 10
  ): Promise<number[]> {
    const hash = await this.generateHmac(serverSeed, clientSeed, nonce);
    const result: number[] = [];

    // 循环提取 Hash 每隔 4 个字符作为一个独立物理骰子
    for (let i = 0; i < reelsCount; i++) {
      const start = i * 4;
      const subHash = hash.substring(start, start + 4);
      const val = parseInt(subHash, 16);
      result.push(val % symbolsPerReel);
    }

    return result;
  }
}
